import { ChangeEvent, FC } from 'react';

import {
    ChevronDownIcon,
    Controller,
    StirlingIcon,
    TextField,
} from '@selina-finance/ui';
import { Formik } from 'formik';
import { pick } from 'lodash';

import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
} from '@Shared/helpers/input';
import { selfEmployedLimitedCompany } from '@src/app/formModels/eligibility/incomings.selfEmployed';
import CallOut from '@src/ui/components/CallOut/Callout';

import ErrorContainer from '../../components/ErrorContainer/ErrorContainer';
import SelectBox from '../../components/SelectBox/SelectBox';

import ExpectedDecrease from './IncomingExpectedDecreaseField';
import IncomingOtherIncomesSubView from './IncomingOtherIncomesSubView';

export interface IncomingSelfEmployedViewLimitedCompanyProps {
    setRef?: Function;
    incomeData?: any;
}

const defaultValues = getDefaultValues(selfEmployedLimitedCompany);
const validationSchema = getValidationSchema(selfEmployedLimitedCompany);

const IncomingSelfEmployedViewLimitedCompany: FC<
    IncomingSelfEmployedViewLimitedCompanyProps
> = (props) => {
    return (
        <Formik
            initialValues={(() => {
                const base = {
                    ...defaultValues,
                    ...pick(
                        props.incomeData,
                        Object.keys(selfEmployedLimitedCompany),
                    ),
                };
                if (base.whenWasCompanyIncorporated && !base.month) {
                    const parts = base.whenWasCompanyIncorporated.split('/');
                    base.month = parts[1] || '';
                    base.year = parts[2] || '';
                }
                return base;
            })()}
            validationSchema={validationSchema}
            onSubmit={async (values) =>
                pick(values, Object.keys(selfEmployedLimitedCompany))
            }
            innerRef={(ref) => {
                if (props.setRef) props.setRef(ref);
            }}
        >
            {(formikProps) => {
                const { getError, getPropsWithError } = getFormHelpers(
                    selfEmployedLimitedCompany,
                    formikProps,
                );
                return (
                    <>
                        <TextField
                            label='What is the company’s registered name?'
                            name='registeredCompanyName'
                            maxLength={255}
                            value={
                                formikProps.values.registeredCompanyName?.toString() ||
                                ''
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            onBlur={formikProps.handleBlur}
                            error={getError('registeredCompanyName')}
                        />
                        <TextField
                            label='How long have you been self-employed?'
                            name='lengthSelfEmployed'
                            mask='number'
                            suffix='Years'
                            maxLength={2}
                            value={
                                formikProps.values.lengthSelfEmployed?.toString() ||
                                ''
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            onBlur={formikProps.handleBlur}
                            error={getError('lengthSelfEmployed')}
                        />
                        <Controller label='When was your company incorporated?'>
                            <div className='grid grid-cols-1 gap-2 grid-cols-2'>
                                <SelectBox
                                    items={
                                        selfEmployedLimitedCompany.month.items
                                    }
                                    suffix={<ChevronDownIcon />}
                                    label='Month'
                                    labelSize='small'
                                    placeholder='Select'
                                    name='month'
                                    validState={!!formikProps.values.month}
                                    value={
                                        formikProps.values.month?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const month = e.currentTarget.value;
                                        formikProps.setTouched({
                                            ...formikProps.touched,
                                            month: true,
                                        });
                                        formikProps.setFieldValue(
                                            'month',
                                            month,
                                        );
                                        if (formikProps.values.year) {
                                            formikProps.setFieldValue(
                                                'whenWasCompanyIncorporated',
                                                `01/${month}/${formikProps.values.year}`,
                                            );
                                        }
                                    }}
                                    error={getError('month')}
                                    onBlur={() => {
                                        return formikProps.setTouched({
                                            ...formikProps.touched,
                                            month: true,
                                        });
                                    }}
                                />
                                <SelectBox
                                    items={
                                        selfEmployedLimitedCompany.year.items
                                    }
                                    suffix={<ChevronDownIcon />}
                                    label='Year'
                                    labelSize='small'
                                    placeholder='Select'
                                    name='year'
                                    validState={!!formikProps.values.year}
                                    value={
                                        formikProps.values.year?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const year = e.currentTarget.value;
                                        formikProps.setFieldValue('year', year);
                                        if (formikProps.values.month) {
                                            formikProps.setFieldValue(
                                                'whenWasCompanyIncorporated',
                                                `01/${formikProps.values.month}/${year}`,
                                            );
                                        }
                                    }}
                                    error={getError('year')}
                                    onBlur={() => {
                                        return formikProps.setTouched({
                                            ...formikProps.touched,
                                            year: true,
                                        });
                                    }}
                                />
                            </div>
                        </Controller>
                        <Controller
                            label='What is your base salary?'
                            additionalInfo='This should be the annual, pre-tax amount'
                            id='base-salary-limited-company'
                        >
                            <CallOut classes='my-4'>
                                <div>
                                    <p className='text-sm_body mt-0.5'>
                                        We’ll verify your income later
                                    </p>
                                </div>
                            </CallOut>
                            <TextField
                                type='tel'
                                prefix={<StirlingIcon />}
                                maxLength={10}
                                mask={'currency' as any}
                                name='incomeAmountSalary'
                                value={
                                    formikProps.values.incomeAmountSalary?.toString() ||
                                    ''
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    formikProps.handleChange(e)
                                }
                                onBlur={formikProps.handleBlur}
                                error={getError('incomeAmountSalary')}
                            />
                        </Controller>
                        <Controller
                            label='Do you earn any other income?'
                            additionalInfo='Such as bonus, dividends, pension, second job.'
                            id='other-income-limited-company'
                        >
                            <IncomingOtherIncomesSubView
                                getError={getError}
                                formikProps={formikProps}
                                getPropsWithError={getPropsWithError}
                                addButtonTitle={
                                    'Add other income from this job'
                                }
                                title={'Other incomes from this job'}
                            />
                        </Controller>
                        <Controller
                            label="At what age do you think you'll retire?"
                            additionalInfo='For most people this will be 66'
                        >
                            <TextField
                                name='estimatedRetirementAge'
                                mask='number'
                                maxLength={2}
                                value={
                                    formikProps.values.estimatedRetirementAge?.toString() ||
                                    ''
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    formikProps.handleChange(e)
                                }
                                onBlur={formikProps.handleBlur}
                                error={getError('estimatedRetirementAge')}
                            />
                        </Controller>
                        <Controller label='Do you expect your income to decrease in the near future?'>
                            <ExpectedDecrease
                                formikProps={formikProps}
                                getPropsWithError={getPropsWithError}
                                getError={getError}
                            />
                        </Controller>
                        <ErrorContainer formikProps={formikProps} />
                    </>
                );
            }}
        </Formik>
    );
};

export default IncomingSelfEmployedViewLimitedCompany;
